import React from 'react';
import Sheet from 'react-modal-sheet';
import { Box, Button, Typography } from '@material-ui/core';
import String from './String';
import { useLanguage } from '../contexts/language';
import { useFocusRestore } from '../hooks/useFocusRestore';

const INSTALL_DISMISSED_KEY = 'route-pwa-install-dismissed';

const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const hasRecentDismissal = () => {
  const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY);
  if (!dismissedAt) return false;
  const diff = Date.now() - parseInt(dismissedAt, 10);
  return diff < DISMISS_DURATION_MS;
};

const PwaInstallButton = () => {
  const deferredPromptRef = React.useRef(null);
  const [showInstallButton, setShowInstallButton] = React.useState(false);
  const installButtonRef = React.useRef(null);
  const { content } = useLanguage();

  React.useEffect(() => {
    if (hasRecentDismissal()) return; // Skip if recently dismissed

    const handler = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = React.useCallback(async () => {
    const deferredPrompt = deferredPromptRef.current;
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.info('PWA installed');
    } else {
      console.info('PWA installation dismissed');
    }

    deferredPromptRef.current = null;
    setShowInstallButton(false);
  }, []);

  React.useEffect(() => {
    if (showInstallButton && !hasRecentDismissal()) {
      setShowInstallButton(true);
    }
  }, [showInstallButton, hasRecentDismissal]);

  const onClose = React.useCallback(() => {
    localStorage.setItem(INSTALL_DISMISSED_KEY, Date.now().toString());
    setShowInstallButton(false);
  }, []);

  useFocusRestore({ isOpen: showInstallButton, focusRef: installButtonRef });

  if (!showInstallButton) return null;

  return (
    <Sheet isOpen={showInstallButton} snapPoints={[0.35, 0]} onClose={onClose}>
      <Sheet.Container
        role="dialog"
        aria-modal="true"
        aria-label={content('aria_pwa_install_dialog')}
        tabIndex={-1}
      >
        <Sheet.Header />
        <Sheet.Content>
          <Box
            width="100%"
            height="100%"
            px={4}
            display="flex"
            flexDirection="column"
            justifyContent="space-evenly"
          >
            <Button onClick={onClose} aria-label={content('aria_dismiss_pwa_install')} className="sr-only">
              <String s="cancel" />
            </Button>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="h6" textAlign="center">
                <String s="pwa_install_cta" />
              </Typography>
              <Typography variant="text">
                <String s="pwa_install_description" />
              </Typography>
            </Box>
            <Button
              ref={installButtonRef}
              fullWidth
              variant="contained"
              onClick={handleInstallClick}
              aria-label={content('install')}
            >
              <String s="install" />
            </Button>
          </Box>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop />
    </Sheet>
  );
};

export default PwaInstallButton;
